import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Users, Mail, Trash2, Crown, ArrowLeft, Copy, Loader2, Plus, Building2, ShieldOff, RotateCcw } from 'lucide-react';

const FOREST = '#2C4A3B';
const TERRA = '#C47A5E';
const CHARCOAL = '#1C1917';

export default function PracticePage({ user, token }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState({ is_enterprise: false, practice: null, role: null });
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [deactivated, setDeactivated] = useState([]);
  const [createName, setCreateName] = useState('');
  const [creating, setCreating] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/practice`, authHeader);
      setCtx(data);
      if (data.practice) {
        const [m, inv, deact] = await Promise.all([
          axios.get(`${API}/practice/members`, authHeader),
          data.role === 'owner'
            ? axios.get(`${API}/practice/invitations`, authHeader)
            : Promise.resolve({ data: { invitations: [] } }),
          data.role === 'owner'
            ? axios.get(`${API}/practice/members/deactivated`, authHeader)
            : Promise.resolve({ data: { deactivated: [] } }),
        ]);
        setMembers(m.data.members || []);
        setInvitations(inv.data.invitations || []);
        setDeactivated(deact.data.deactivated || []);
      } else {
        setMembers([]);
        setInvitations([]);
        setDeactivated([]);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load practice');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createName.trim()) return;
    setCreating(true);
    try {
      await axios.post(`${API}/practice`, { name: createName.trim() }, authHeader);
      toast.success('Practice created');
      setCreateName('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create practice');
    } finally {
      setCreating(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const { data } = await axios.post(
        `${API}/practice/invite`,
        { email: inviteEmail.trim(), full_name: inviteName.trim() || undefined },
        authHeader,
      );
      toast.success('Invitation sent');
      if (data?.invitation?.accept_url) {
        try { await navigator.clipboard.writeText(window.location.origin + data.invitation.accept_url.replace(/^https?:\/\/[^/]+/, '')); } catch {}
      }
      setInviteEmail('');
      setInviteName('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleCancelInvite = async (id) => {
    try {
      await axios.delete(`${API}/practice/invitations/${id}`, authHeader);
      toast.success('Invitation cancelled');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel invite');
    }
  };

  const handleRevoke = async (userId, name) => {
    const label = name || 'this clinician';
    if (!window.confirm(
      `Revoke access for ${label}?\n\n` +
      `• They will no longer be able to sign in to Plexura.\n` +
      `• They'll be removed from your practice roster.\n` +
      `• Their patients and sessions will be preserved for HIPAA compliance.\n\n` +
      `You can restore access later from the Deactivated clinicians section.`
    )) return;
    try {
      await axios.delete(`${API}/practice/members/${userId}`, authHeader);
      toast.success('Access revoked — clinician can no longer sign in');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not revoke access');
    }
  };

  const handleRestore = async (userId, name) => {
    if (!window.confirm(`Restore access for ${name || 'this clinician'}? They'll be able to sign in and will be added back to your practice.`)) return;
    try {
      await axios.post(`${API}/practice/members/${userId}/restore`, {}, authHeader);
      toast.success('Access restored');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not restore access');
    }
  };

  const copyInvite = async (token) => {
    const url = `${window.location.origin}/accept-invite/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Invite link copied');
    } catch {
      toast.error('Copy failed — here is the link: ' + url);
    }
  };

  // ── Gate ─────────────────────────────────────────────
  if (!loading && !ctx.is_enterprise) {
    return (
      <div className="min-h-screen" style={{ background: '#F9F6F0', fontFamily: "'Manrope', sans-serif" }}>
        <div className="max-w-2xl mx-auto px-6 py-20">
          <div className="bg-white rounded-3xl border border-stone-200 p-10 text-center shadow-sm" data-testid="practice-enterprise-gate">
            <div className="w-14 h-14 rounded-2xl mx-auto mb-5 flex items-center justify-center" style={{ background: FOREST }}>
              <Users className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ color: CHARCOAL }}>Team management is an Enterprise feature</h1>
            <p className="text-sm text-stone-500 mb-6">
              Invite additional clinicians, manage roles, and centralize billing across your practice. Available on the Enterprise plan for group practices with 50+ clinicians.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => navigate('/contact')} className="h-11 rounded-xl text-white font-semibold" style={{ background: TERRA }} data-testid="practice-talk-to-sales">
                Talk to Sales
              </Button>
              <Button onClick={() => navigate('/')} variant="outline" className="h-11 rounded-xl" data-testid="practice-back-btn">
                Back to dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isOwner = ctx.role === 'owner';

  return (
    <div className="min-h-screen" style={{ background: '#F9F6F0', fontFamily: "'Manrope', sans-serif" }} data-testid="practice-page">
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-stone-900">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </button>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-[0.2em] px-2 py-1 rounded-full" style={{ background: '#E3E8E4', color: FOREST }}>
              Enterprise
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3" style={{ color: CHARCOAL }}>
            <Building2 className="w-7 h-7" style={{ color: FOREST }} />
            {ctx.practice ? ctx.practice.name : 'Your Practice'}
          </h1>
          <p className="text-sm text-stone-500 mt-1">Manage your team, invite clinicians, and control access.</p>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-stone-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        )}

        {!loading && !ctx.practice && (
          <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 shadow-sm" data-testid="create-practice-card">
            <h2 className="text-lg font-semibold mb-1" style={{ color: CHARCOAL }}>Create your practice</h2>
            <p className="text-sm text-stone-500 mb-5">Name your practice. You'll be able to invite clinicians once it's set up.</p>
            <form onSubmit={handleCreate} className="flex flex-col sm:flex-row gap-3">
              <Input
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="Evergreen Behavioral Health"
                required
                data-testid="practice-name-input"
                className="h-11 rounded-xl flex-1"
              />
              <Button type="submit" disabled={creating} className="h-11 rounded-xl text-white font-semibold px-6" style={{ background: FOREST }} data-testid="create-practice-btn">
                {creating ? 'Creating…' : <><Plus className="w-4 h-4 mr-1" />Create Practice</>}
              </Button>
            </form>
          </div>
        )}

        {!loading && ctx.practice && (
          <>
            {isOwner && (
              <div className="py-2" data-testid="invite-card">
                <h2 className="text-lg font-semibold mb-1 flex items-center gap-2" style={{ color: CHARCOAL }}>
                  <Mail className="w-5 h-5" style={{ color: TERRA }} /> Invite a clinician
                </h2>
                <p className="text-sm text-stone-500 mb-5">They'll get an email invitation. Link expires in 14 days.</p>
                <form onSubmit={handleInvite} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3">
                  <div>
                    <Label htmlFor="inv_email" className="sr-only">Email</Label>
                    <Input id="inv_email" type="email" required value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="clinician@yourclinic.com"
                      data-testid="invite-email-input"
                      className="h-11 rounded-xl" />
                  </div>
                  <div>
                    <Label htmlFor="inv_name" className="sr-only">Full name</Label>
                    <Input id="inv_name" value={inviteName}
                      onChange={(e) => setInviteName(e.target.value)}
                      placeholder="Full name (optional)"
                      data-testid="invite-name-input"
                      className="h-11 rounded-xl" />
                  </div>
                  <Button type="submit" disabled={inviting} className="h-11 rounded-xl text-white font-semibold px-6" style={{ background: FOREST }} data-testid="invite-submit-btn">
                    {inviting ? 'Sending…' : 'Send invite'}
                  </Button>
                </form>
              </div>
            )}

            {/* Members roster */}
            <div className="py-2" data-testid="members-card">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: CHARCOAL }}>
                <Users className="w-5 h-5" style={{ color: FOREST }} /> Clinicians
                <span className="text-xs font-normal text-stone-500">({members.length})</span>
              </h2>
              <div className="divide-y divide-stone-100">
                {members.map((m) => (
                  <div key={m.id} className="flex items-center justify-between py-3" data-testid={`member-row-${m.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold shrink-0" style={{ background: FOREST }}>
                        {(m.full_name || m.email).trim().charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: CHARCOAL }}>{m.full_name || m.email}</p>
                        <p className="text-xs text-stone-500 truncate">{m.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {m.role === 'owner' ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full" style={{ background: '#FDECE2', color: TERRA }}>
                          <Crown className="w-3 h-3" /> Owner
                        </span>
                      ) : (
                        <span className="text-[11px] font-medium text-stone-500 px-2 py-1">Member</span>
                      )}
                      {isOwner && m.role !== 'owner' && (
                        <button
                          onClick={() => handleRevoke(m.id, m.full_name || m.email)}
                          data-testid={`revoke-member-${m.id}`}
                          className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors"
                          title="Revoke access — deactivates login + removes from practice (reversible)"
                        >
                          <ShieldOff className="w-3.5 h-3.5" />
                          Revoke access
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pending invites */}
            {isOwner && invitations.length > 0 && (
              <div className="py-2" data-testid="invitations-card">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: CHARCOAL }}>
                  <Mail className="w-5 h-5" style={{ color: TERRA }} /> Pending invitations
                </h2>
                <div className="divide-y divide-stone-100">
                  {invitations.map((inv) => (
                    <div key={inv.id} className="flex flex-wrap items-center justify-between py-3 gap-3" data-testid={`invitation-row-${inv.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: CHARCOAL }}>{inv.email}</p>
                        <p className="text-[11px] text-stone-500">
                          Status: <span className="capitalize">{inv.status}</span>
                          {inv.expires_at && ` · expires ${new Date(inv.expires_at).toLocaleDateString()}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {inv.status === 'pending' && inv.token && (
                          <button
                            onClick={() => copyInvite(inv.token)}
                            data-testid={`copy-invite-${inv.id}`}
                            className="inline-flex items-center gap-1 text-xs font-medium text-stone-600 hover:text-stone-900 px-2 py-1 rounded-lg hover:bg-stone-50"
                          >
                            <Copy className="w-3.5 h-3.5" /> Copy link
                          </button>
                        )}
                        {inv.status === 'pending' && (
                          <button
                            onClick={() => handleCancelInvite(inv.id)}
                            data-testid={`cancel-invite-${inv.id}`}
                            className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-50"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Deactivated clinicians */}
            {isOwner && deactivated.length > 0 && (
              <div className="py-2" data-testid="deactivated-card">
                <h2 className="text-lg font-semibold mb-1 flex items-center gap-2" style={{ color: CHARCOAL }}>
                  <ShieldOff className="w-5 h-5 text-red-600" /> Deactivated clinicians
                  <span className="text-xs font-normal text-stone-500">({deactivated.length})</span>
                </h2>
                <p className="text-xs text-stone-500 mb-4">Access revoked. Their patients and sessions remain in the database for HIPAA compliance. Restore access anytime.</p>
                <div className="divide-y divide-stone-100">
                  {deactivated.map((d) => (
                    <div key={d.id} className="flex flex-wrap items-center justify-between py-3 gap-3" data-testid={`deactivated-row-${d.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold shrink-0 opacity-60" style={{ background: '#8A8A80' }}>
                          {(d.full_name || d.email).trim().charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate" style={{ color: CHARCOAL }}>{d.full_name || d.email}</p>
                          <p className="text-[11px] text-stone-500 truncate">
                            {d.email}
                            {d.deactivated_at && ` · revoked ${new Date(d.deactivated_at).toLocaleDateString()}`}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRestore(d.id, d.full_name || d.email)}
                        data-testid={`restore-member-${d.id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                        style={{ background: '#E3E8E4', color: FOREST }}
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        Restore access
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
